/**
 * FP3-033A — Display-mode goal progress graph card.
 *
 * Reusable read-only chart card for the Student Goal Sheet Display
 * mode. Pulls trial runs from `GET /api/progress/goal/{id}` and
 * renders:
 *   - calm metric row (Baseline / Current / Target / Recent avg)
 *   - large progress chart with Target + Baseline reference lines
 *   - calm empty state when there's no chartable data
 *
 * Deliberately omitted (per spec):
 *   - Individual data-point table
 *   - Add Entry / edit controls
 *   - Provider dashboard chrome
 *
 * Privacy: the underlying endpoint enforces the same student
 * visibility rules used by the rest of the app — no new surface.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "@/lib/api";

const ACCENT = "#1E3F33";
const ACCENT_LINE = "#2F6B4F";
const BASELINE_COLOR = "#9B9890";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function parseBaselinePct(goal) {
  // Goals may store baseline either as a numeric `baseline_mastery`
  // or as a free-text `baseline` that ends with a percentage. Match
  // the FP3-020 GoalProgress card behavior so both display
  // identically.
  if (
    goal?.baseline_mastery !== undefined &&
    goal?.baseline_mastery !== null &&
    goal?.baseline_mastery !== ""
  ) {
    const n = Number(goal.baseline_mastery);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  const m = (goal?.baseline || "").match(/(\d+)\s*%/);
  if (m) return parseInt(m[1], 10);
  return 0;
}

export default function DisplayGoalProgressCard({ goal, windowDays = 90, testIdPrefix }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.getGoalProgress(goal.id, { days: windowDays })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message || "Could not load progress");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [goal.id, windowDays]);

  const chartData = useMemo(() => {
    const runs = data?.trial_runs || [];
    return runs.map((r) => ({
      date: fmtDate(r.date),
      rawDate: r.date,
      accuracy: r.accuracy,
      trials: r.trials,
      source: r.source || "live_data",
    }));
  }, [data]);

  const cur = goal.current_mastery ?? data?.goal?.current_mastery ?? 0;
  const target = goal.target_mastery ?? data?.goal?.target_mastery ?? 80;
  const baseAcc = parseBaselinePct(data?.goal || goal);
  const recentAvg = data?.computed_trend?.recent_avg;
  const narrativeEvidence = (data?.evidence || []).filter((e) => !e.chartable);

  return (
    <div data-testid={testIdPrefix}>
      {/* Metric row — calm, no tiny labels */}
      <div
        data-testid={`${testIdPrefix}-metrics`}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 14,
          marginTop: 16,
          marginBottom: 18,
        }}
      >
        {[
          { label: "Baseline", value: baseAcc ? `${baseAcc}%` : "—", testid: "baseline" },
          { label: "Current", value: `${cur}%`, testid: "current" },
          { label: "Target", value: `${target}%`, testid: "target" },
          {
            label: "Recent avg",
            value: recentAvg !== null && recentAvg !== undefined ? `${recentAvg}%` : "—",
            testid: "recent-avg",
          },
        ].map((m) => (
          <div
            key={m.label}
            data-testid={`${testIdPrefix}-metric-${m.testid}`}
            style={{
              background: "var(--fp-print-bg-soft)",
              border: "1px solid var(--fp-print-line)",
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <div
              className="fp-display-label"
              style={{ marginBottom: 4 }}
            >
              {m.label}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color: m.testid === "current" ? ACCENT_LINE : "var(--fp-print-ink)",
              }}
            >
              {m.value}
            </div>
          </div>
        ))}
      </div>

      {/* Chart card */}
      <div
        data-testid={`${testIdPrefix}-chart`}
        style={{
          background: "#fff",
          border: "1px solid var(--fp-print-line)",
          borderRadius: 14,
          padding: 16,
          minHeight: 360,
        }}
      >
        {loading ? (
          <div
            data-testid={`${testIdPrefix}-chart-loading`}
            style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--fp-print-soft)",
              fontSize: 14,
            }}
          >
            Loading progress…
          </div>
        ) : error ? (
          <div
            data-testid={`${testIdPrefix}-chart-error`}
            style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              color: "var(--fp-print-soft)",
              fontSize: 14,
              gap: 6,
            }}
          >
            <span>We couldn’t load the progress chart for this goal.</span>
          </div>
        ) : chartData.length === 0 ? (
          <div
            data-testid={`${testIdPrefix}-chart-empty`}
            style={{
              height: 320,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              color: "var(--fp-print-soft)",
              fontSize: 16,
              padding: "0 24px",
              textAlign: "center",
            }}
          >
            <span style={{ fontWeight: 600, color: "var(--fp-print-ink)", fontSize: 17 }}>
              No chartable data yet.
            </span>
            {narrativeEvidence.length > 0 ? (
              <span>
                Narrative evidence is on file ({narrativeEvidence.length}{" "}
                {narrativeEvidence.length === 1 ? "entry" : "entries"}); no
                trial data has been logged yet.
              </span>
            ) : (
              <span>Log live or quick-session data to plot progress over time.</span>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart
              data={chartData}
              margin={{ top: 12, right: 36, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient
                  id={`accFill-display-${goal.id}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={ACCENT_LINE} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={ACCENT_LINE} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 13, fill: "var(--fp-print-soft)" }}
                tickLine={false}
                axisLine={{ stroke: "#E5E3DB" }}
                minTickGap={24}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 13, fill: "var(--fp-print-soft)" }}
                tickLine={false}
                axisLine={{ stroke: "#E5E3DB" }}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #E5E3DB",
                  borderRadius: 10,
                  fontSize: 13,
                }}
                formatter={(v) => [`${v}%`, "Accuracy"]}
              />
              <ReferenceLine
                y={target}
                stroke={ACCENT}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: `Target ${target}%`,
                  position: "right",
                  fontSize: 12,
                  fill: ACCENT,
                }}
              />
              {baseAcc > 0 && (
                <ReferenceLine
                  y={baseAcc}
                  stroke={BASELINE_COLOR}
                  strokeDasharray="2 4"
                  strokeWidth={1.25}
                  label={{
                    value: `Baseline ${baseAcc}%`,
                    position: "right",
                    fontSize: 12,
                    fill: BASELINE_COLOR,
                  }}
                />
              )}
              <Area
                type="monotone"
                dataKey="accuracy"
                stroke="none"
                fill={`url(#accFill-display-${goal.id})`}
              />
              <Line
                type="monotone"
                dataKey="accuracy"
                stroke={ACCENT_LINE}
                strokeWidth={2.5}
                dot={{ r: 4, fill: ACCENT_LINE }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
